import { brandsData } from './genericData';

/**
 * Static reference/seed data for the Styles mock (`styles.ts`) — season/group/supplier/
 * contact-person lookups and the shared row/custom-field row shapes `generateStyle` and the
 * bulk-fixture builders (`stylesBulkFixtureData.ts`) draw from. Split out of `styles.ts` purely
 * to keep that file under the repo's 800-line cap (`.eslintrc.js` `max-lines`); no behavior
 * change, same values, same shapes.
 */

export const seasons = [
  { id: 26, customId: 'AW25', name: 'AW25', project: '' },
  { id: 28, customId: 'AW26', name: 'AW26', project: '' },
  { id: 27, customId: 'SS25', name: 'SS25', project: '' },
  { id: 25, customId: 'SS26', name: 'SS26', project: '' },
];

export const groups = [
  { id: 71, customId: 'MEN', name: 'Menswear' },
  { id: 72, customId: 'WMN', name: 'Womenswear' },
  { id: 73, customId: 'KID', name: 'Kidswear' },
];

export const categories = [
  { id: 87, customId: 'TOP', name: 'Tops' },
  { id: 88, customId: 'BOT', name: 'Bottoms' },
  { id: 89, customId: 'OUT', name: 'Outerwear' },
  { id: 90, customId: 'KNT', name: 'Knitwear' },
];

export const suppliers = [
  { id: 44, customId: 'S-44', name: 'Ningbo garment supplier' },
  { id: 45, customId: 'S-45', name: 'Shanghai apparel co.' },
  { id: 46, customId: 'S-46', name: 'Guangzhou textile factory' },
];

export const contactPersons = [
  { id: 'd47e406b-d5db-4787-ade6-7225780b5b5c', name: 'Sara Johnson' },
  { id: 'a12b3c4d-e5f6-7890-abcd-ef1234567890', name: 'Mike Chen' },
  { id: 'b23c4d5e-f6a7-8901-bcde-f12345678901', name: 'Anna Larsen' },
  { id: 'c34d5e6f-a7b8-9012-cdef-123456789012', name: 'James Park' },
  { id: 'e45f6a7b-8c9d-0123-def0-234567890123', name: 'Lina Sorensen' },
  { id: 'f56a7b8c-9d0e-1234-ef01-345678901234', name: 'Tom Becker' },
];

/**
 * Brands the styles are assigned to. Reuses the same brand list the brand filter
 * combobox is populated from (`GET /api/brands` in genericData), so a filter value
 * picked from the combobox always corresponds to seeded styles. genericData seeds
 * at least 3 brands, satisfying the epic's "styles across ≥3 brands" requirement.
 */
export const brandOptions =
  brandsData.length > 0
    ? brandsData
    : [
        { id: 29, name: 'Brand A', userDefinedId: '1' },
        { id: 30, name: 'Brand B', userDefinedId: '2' },
        { id: 31, name: 'Brand C', userDefinedId: '3' },
      ];

export type StyleRow = {
  id: number;
  name: string;
  customId: string;
  description: string | null;
  state: string;
  logo: string;
  season: { id: number; customId: string; name: string; project: string } | null;
  group: { id: number; customId: string; name: string } | null;
  brand: {
    id: number;
    customId: string;
    name: string;
    contactPersonId: string;
    contactPersonName: string;
  } | null;
  supplier: {
    id: number;
    customId: string;
    name: string;
    contactPersonId: string;
    contactPersonName: string;
  } | null;
  categories: Array<{ id: number; customId: string; name: string }>;
  styleColors: Array<{ id: number; name: string; customId1: string; customId2: string }>;
  customFields: CustomFieldRowEntry[];
  properties: string[];
  lastUpdated: string;
  readyForExport: boolean;
};

/**
 * Wire shape for a style's `customFields[]` entry — mirrors the real API's `ApiCustomField`
 * (`src/features/styles/types/Style.ts`), NOT the FE-internal flat `CustomField`/
 * `CustomFieldEntry` shape. `getStyles.ts` transforms this nested shape into the flat one via
 * `transformApiCustomField` on every GET — a mock that emits the flat shape directly produces
 * garbage after that transform (customFieldId read from the wrong field, value unwrapped from
 * a non-existent `.value.value`), silently rendering every CF cell empty.
 */
export type CustomFieldRowEntry = {
  id: number; // customFieldId
  type: 'text' | 'date' | 'allowedvalue' | 'nestedallowedvalue' | 'numeric' | 'divider';
  name?: string;
  appliesToStyle?: boolean;
  value?: { id: number; value: string | null; customId?: string | null } | null;
  values?: Array<{ id: number; value: string | null; customId?: string | null }>;
};
